// +----------------------------------------------------------------------
// | Axios 实例：统一 baseURL、双 Header 注入、响应解包、401 拦截
// | 约定：后端返回 { code, msg, data }
// |   code === 0      → resolve(data)
// |   code === 401    → 清登录态并跳登录页
// |   其他             → reject(Error(msg))
// +----------------------------------------------------------------------
import axios from 'axios';
import { getAuth, clearAuth } from './storage';
import { API_BASE } from './config';

const request = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

// ------------------------------ 请求拦截：注入鉴权双 Header ------------------------------
request.interceptors.request.use(
  (config) => {
    const auth = getAuth();
    if (auth) {
      config.headers.username = auth.username;
      config.headers.token = auth.token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function handleUnauthorized() {
  clearAuth();
  const { pathname } = window.location;
  if (pathname !== '/login' && pathname !== '/register') {
    window.location.replace('/login');
  }
}

// ------------------------------ 响应拦截：解包 + 统一错误 ------------------------------
request.interceptors.response.use(
  (response) => {
    const body = response.data;

    // 非标准结构（比如被 nginx 返回了 HTML），直接当异常
    if (!body || typeof body !== 'object') {
      return Promise.reject(new Error('返回数据格式异常'));
    }

    if (body.code === 0) {
      return body.data;
    }

    if (body.code === 401) {
      handleUnauthorized();
      return Promise.reject(new Error(body.msg || '登录已过期，请重新登录'));
    }

    return Promise.reject(new Error(body.msg || '请求失败'));
  },
  (error) => {
    const status = error.response && error.response.status;
    const body = error.response && error.response.data;

    if (status === 401) {
      handleUnauthorized();
      return Promise.reject(new Error('登录已过期，请重新登录'));
    }

    if (body && typeof body === 'object' && body.msg) {
      return Promise.reject(new Error(body.msg));
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('想得有点久，稍后再试一下'));
    }

    return Promise.reject(new Error('网络不太顺，稍后再试'));
  }
);

export default request;
